import type { Note, NotePhoto } from '@/types/note';
import type { Paginated } from '@/types/pagination';
import type {
  NotePhotoWire,
  NoteWire,
  PaginatedNoteResponseWire,
} from '../schemas/note';
import { mapPhotoMetaWire } from './photo';

/**
 * Convert a note photo from wire format to domain format.
 * Maps snake_case to camelCase and the ISO timestamp to Unix milliseconds.
 */
export function mapNotePhotoWireToDomain(wire: NotePhotoWire): NotePhoto {
  return {
    id: wire.id,
    noteId: wire.note_id,
    r2Key: wire.r2_key,
    imageUrl: wire.image_url,
    createdAt: new Date(wire.created_at).getTime(),
    ...mapPhotoMetaWire(wire),
  };
}

/**
 * Convert a note from wire format to domain format.
 * Timestamps become Unix millisecond numbers; optional JOIN fields map to
 * camelCase optionals; an undefined or null photo normalizes to null.
 */
export function mapNoteWireToDomain(wire: NoteWire): Note {
  return {
    id: wire.id,
    userId: wire.user_id,
    businessId: wire.business_id,
    menuItemId: wire.menu_item_id,
    content: wire.content,
    createdAt: new Date(wire.created_at).getTime(),
    updatedAt: new Date(wire.updated_at).getTime(),
    businessName: wire.business_name,
    menuItemName: wire.menu_item_name,
    photo: wire.photo ? mapNotePhotoWireToDomain(wire.photo) : null,
  };
}

/**
 * Convert a paginated notes response from wire format to the domain
 * Paginated<Note> envelope (camelCase).
 */
export function mapPaginatedNotesWireToDomain(
  wire: PaginatedNoteResponseWire
): Paginated<Note> {
  return {
    items: wire.items.map(mapNoteWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}
